import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, User, MessageSquare, ArrowLeft, Share2, Paintbrush, CheckCircle, Tag, Star, Truck, Flag, AlertTriangle, GraduationCap, Wrench, Eye, Globe, Calendar, Users as UsersIcon, Clock, ExternalLink, RotateCw } from 'lucide-react';

// Compute the next upcoming session date for a recurring workshop
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FREQ_LABELS = { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' };

const computeNextSession = (listing) => {
  if (!listing?.is_recurring) return null;
  const now = new Date();
  const start = listing.workshop_date ? new Date(listing.workshop_date) : null;
  const end = listing.recurrence_end_date ? new Date(listing.recurrence_end_date) : null;
  const freq = listing.recurrence_frequency || 'weekly';

  // If we have a start date, roll it forward until we're >= now
  let next = start ? new Date(start) : new Date(now);
  if (!start) {
    // Find the next occurrence of the day-of-week from today
    const dow = listing.recurrence_day_of_week;
    if (dow === null || dow === undefined) return null;
    // JS: 0=Sun..6=Sat; our storage: 0=Mon..6=Sun. Convert.
    const jsDow = (dow + 1) % 7;
    next = new Date(now);
    const daysUntil = (jsDow - next.getDay() + 7) % 7;
    next.setDate(next.getDate() + daysUntil);
  }

  const stepDays = freq === 'weekly' ? 7 : freq === 'fortnightly' ? 14 : null;
  let guard = 0;
  while (next < now && guard++ < 400) {
    if (freq === 'monthly') {
      next.setMonth(next.getMonth() + 1);
    } else if (stepDays) {
      next.setDate(next.getDate() + stepDays);
    } else {
      break;
    }
  }
  if (end && next > end) return null;
  return next;
};
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import ImageGallery from '../components/ImageGallery';
import { toast } from 'sonner';
import { SellerRatingSummary, ReviewForm, ReviewsList } from '../components/ReviewSystem';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';

import { API_URL } from '../utils/api';

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [listing, setListing] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contacting, setContacting] = useState(false);
  const [customOrderOpen, setCustomOrderOpen] = useState(false);
  const [customOrderData, setCustomOrderData] = useState({ description: '', budget: '' });
  const [sendingCustomOrder, setSendingCustomOrder] = useState(false);
  const [markingSold, setMarkingSold] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/listings/${id}`);
      setListing(res.data);
      
      // Fetch seller profile to check if they accept custom orders
      try {
        const sellerRes = await axios.get(`${API_URL}/api/seller/${res.data.seller_id}/profile`);
        setSellerProfile(sellerRes.data);
      } catch (err) {
        console.error('Failed to fetch seller profile', err);
      }
    } catch (err) {
      console.error('Failed to fetch listing', err);
      toast.error('Listing not found');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleContact = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/listing/${id}` } });
      return;
    }

    if (user.id === listing.seller_id) {
      toast.error("You can't message yourself");
      return;
    }

    setContacting(true);
    try {
      const res = await api().post(`/conversations?listing_id=${id}`);
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      console.error('Failed to start conversation', err);
      toast.error('Failed to start conversation');
    } finally {
      setContacting(false);
    }
  };

  const handleCustomOrderRequest = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/listing/${id}` } });
      return;
    }

    if (!customOrderData.description.trim()) {
      toast.error('Please describe what you\'d like');
      return;
    }

    setSendingCustomOrder(true);
    try {
      const res = await api().post('/custom-order/request', {
        seller_id: listing.seller_id,
        description: customOrderData.description,
        budget: customOrderData.budget ? parseFloat(customOrderData.budget) : null
      });
      toast.success('Custom order request sent!');
      setCustomOrderOpen(false);
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      console.error('Failed to send custom order request', err);
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSendingCustomOrder(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `Check out this item on Crafters Market: ${listing.title}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const handleMarkSold = async () => {
    setMarkingSold(true);
    try {
      await api().put(`/listings/${id}/sold`);
      toast.success('Item marked as sold!');
      setListing({ ...listing, status: 'sold' });
    } catch (err) {
      console.error('Failed to mark as sold', err);
      toast.error('Failed to mark as sold');
    } finally {
      setMarkingSold(false);
    }
  };

  const handleMarkUnsold = async () => {
    setMarkingSold(true);
    try {
      await api().put(`/listings/${id}/unsold`);
      toast.success('Item marked as available!');
      setListing({ ...listing, status: 'active' });
    } catch (err) {
      console.error('Failed to mark as available', err);
      toast.error('Failed to mark as available');
    } finally {
      setMarkingSold(false);
    }
  };

  const handleReportListing = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for your report');
      return;
    }
    
    setSubmittingReport(true);
    try {
      await api().post(`/listings/${id}/report`, {
        reason: reportReason,
        details: reportDetails
      });
      toast.success('Report submitted. The seller has been notified to review their listing.');
      setReportOpen(false);
      setReportReason('');
      setReportDetails('');
    } catch (err) {
      console.error('Failed to report listing', err);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
      </div>
    );
  }

  if (!listing) return null;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to listings
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3"
          >
            <ImageGallery
              images={listing.images}
              beforeImages={listing.before_images}
              afterImages={listing.after_images}
              isRestoration={listing.is_restoration}
            />
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* SOLD Banner */}
            {listing.status === 'sold' && (
              <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white px-4 py-3 rounded-sm flex items-center gap-2">
                <Tag className="h-5 w-5" />
                <span className="font-bold">SOLD</span>
                <span className="text-sky-100 text-sm ml-2">This item has been sold, but you can still message the seller about similar items or custom orders.</span>
              </div>
            )}

            {/* Title and Price */}
            <div>
              {listing.is_restoration && (
                <span className="badge-primary mb-2 inline-block">Restored</span>
              )}
              {listing.listing_type === 'hands_on_workshop' && (
                <span className="inline-flex items-center gap-1 mb-2 px-2.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                  <Wrench className="h-3 w-3" /> Hands-on Workshop
                  {listing.workshop_format === 'online' && <><Globe className="h-3 w-3 ml-1" /> Online</>}
                  {listing.workshop_format === 'in_person' && <><MapPin className="h-3 w-3 ml-1" /> In-Person</>}
                  {listing.workshop_format === 'both' && <span className="ml-1">• Online or In-Person</span>}
                </span>
              )}
              {listing.listing_type === 'look_and_learn' && (
                <span className="inline-flex items-center gap-1 mb-2 px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
                  <Eye className="h-3 w-3" /> Look & Learn
                  {listing.workshop_format === 'online' && <><Globe className="h-3 w-3 ml-1" /> Online</>}
                  {listing.workshop_format === 'in_person' && <><MapPin className="h-3 w-3 ml-1" /> In-Person</>}
                  {listing.workshop_format === 'both' && <span className="ml-1">• Online or In-Person</span>}
                </span>
              )}
              <h1 className={`text-2xl lg:text-3xl font-bold ${listing.status === 'sold' ? 'text-stone-500' : 'text-stone-900'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                {listing.title}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                <span className={`price-tag text-3xl ${listing.status === 'sold' ? 'text-stone-400 line-through' : ''}`}>
                  ${listing.price.toFixed(2)}
                  {(listing.listing_type === 'hands_on_workshop' || listing.listing_type === 'look_and_learn') && (
                    <span className="text-sm text-stone-400 font-normal ml-1">/ seat</span>
                  )}
                </span>
                {listing.listing_type === 'item' && (
                  <span className="text-sm text-stone-400 capitalize px-2 py-1 bg-stone-100 rounded-sm">
                    {listing.condition?.replace('-', ' ')}
                  </span>
                )}
              </div>
            </div>

            {/* Workshop Details Card */}
            {(listing.listing_type === 'hands_on_workshop' || listing.listing_type === 'look_and_learn') && (
              <div className="bg-gradient-to-br from-teal-50 to-sky-50 border border-teal-100 rounded-sm p-5 space-y-3" data-testid="workshop-details-card">
                <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-teal-600" />
                  Workshop Details
                  {listing.is_recurring && (
                    <span className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 bg-teal-600 text-white text-[10px] font-bold uppercase rounded-full" data-testid="recurring-badge">
                      <RotateCw className="h-3 w-3" /> Recurring
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {/* Recurring: show NEXT session prominently */}
                  {listing.is_recurring ? (() => {
                    const next = computeNextSession(listing);
                    return (
                      <>
                        <div className="flex items-center gap-2 text-stone-700">
                          <RotateCw className="h-4 w-4 text-teal-600 flex-shrink-0" />
                          <span>
                            {FREQ_LABELS[listing.recurrence_frequency] || 'Recurring'}
                            {listing.recurrence_day_of_week !== null && listing.recurrence_day_of_week !== undefined ? ` · ${DAY_NAMES[listing.recurrence_day_of_week]}s` : ''}
                          </span>
                        </div>
                        {next && (
                          <div className="flex items-center gap-2 text-stone-700 sm:col-span-2 bg-white/60 rounded p-2 border border-teal-100">
                            <Calendar className="h-4 w-4 text-teal-600 flex-shrink-0" />
                            <span><strong>Next session:</strong> {next.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {!next && (
                          <div className="flex items-center gap-2 text-stone-700 sm:col-span-2">
                            <Calendar className="h-4 w-4 text-teal-600 flex-shrink-0" />
                            <span className="italic text-stone-500">Series ended — message host for future dates</span>
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    listing.workshop_date ? (
                      <div className="flex items-center gap-2 text-stone-700">
                        <Calendar className="h-4 w-4 text-teal-600 flex-shrink-0" />
                        <span>{new Date(listing.workshop_date).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-stone-700">
                        <Calendar className="h-4 w-4 text-teal-600 flex-shrink-0" />
                        <span className="italic text-stone-500">By appointment — message host</span>
                      </div>
                    )
                  )}
                  {listing.workshop_duration && (
                    <div className="flex items-center gap-2 text-stone-700">
                      <Clock className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      <span>{listing.workshop_duration}</span>
                    </div>
                  )}
                  {listing.workshop_capacity && (
                    <div className="flex items-center gap-2 text-stone-700">
                      <UsersIcon className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      <span>{listing.workshop_capacity} {listing.workshop_capacity === 1 ? 'seat' : 'seats'} available</span>
                    </div>
                  )}
                  {listing.workshop_format && (
                    <div className="flex items-center gap-2 text-stone-700">
                      {listing.workshop_format === 'online' ? <Globe className="h-4 w-4 text-teal-600 flex-shrink-0" /> :
                       listing.workshop_format === 'both' ? <GraduationCap className="h-4 w-4 text-teal-600 flex-shrink-0" /> :
                       <MapPin className="h-4 w-4 text-teal-600 flex-shrink-0" />}
                      <span>
                        {listing.workshop_format === 'both' ? 'Online or In-Person' :
                         listing.workshop_format === 'in_person' ? 'In-Person' : 'Online'}
                      </span>
                    </div>
                  )}
                </div>
                {listing.workshop_external_link && (
                  <a
                    href={listing.workshop_external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-white border border-teal-200 hover:border-teal-400 hover:bg-teal-50 text-teal-700 text-sm font-medium rounded-sm transition-colors"
                    data-testid="workshop-external-link"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {listing.workshop_format === 'online' ? 'Open meeting / booking page' : 'Booking / info page'}
                  </a>
                )}
                <p className="text-xs text-stone-500 pt-2 border-t border-teal-100">
                  Crafters Market doesn't process workshop bookings. Message the host or use the link above to register.
                </p>
              </div>
            )}

            {/* Location */}
            <div className="flex items-center gap-2 text-stone-500">
              <MapPin className="h-4 w-4" />
              <span>{listing.location}</span>
              {listing.area && (
                <>
                  <span className="text-stone-300">•</span>
                  <span className="text-sm">{listing.area}</span>
                </>
              )}
            </div>

            {/* Pickup/Delivery Info */}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 text-stone-600 rounded-full">
                <MapPin className="h-3.5 w-3.5" />
                Local pickup
              </span>
              {listing.offers_delivery && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full">
                  <Truck className="h-3.5 w-3.5" />
                  Delivery available
                </span>
              )}
            </div>
            {listing.offers_delivery && (
              <p className="text-xs text-stone-500 -mt-2">
                Delivery/shipping costs to be arranged directly with seller
              </p>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <div className="flex gap-3">
                <Button
                  onClick={handleContact}
                  disabled={contacting || user?.id === listing.seller_id}
                  className="flex-1 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide btn-active"
                  data-testid="contact-seller-btn"
                >
                  {contacting ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  ) : (
                    <MessageSquare className="h-4 w-4 mr-2" />
                  )}
                  {user?.id === listing.seller_id ? 'Your Listing' : ((listing.listing_type === 'hands_on_workshop' || listing.listing_type === 'look_and_learn') ? 'Message Host' : 'Contact Seller')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="border-stone-200"
                  data-testid="share-btn"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Custom Order Button */}
              {sellerProfile?.accepts_custom_orders && user?.id !== listing.seller_id && (
                <Dialog open={customOrderOpen} onOpenChange={setCustomOrderOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full border-teal-200 text-teal-600 hover:bg-teal-50"
                      data-testid="custom-order-btn"
                    >
                      <Paintbrush className="h-4 w-4 mr-2" />
                      Request Custom Order
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                        Request a Custom Piece
                      </DialogTitle>
                      <DialogDescription>
                        {listing.seller_name} accepts custom orders! Describe what you're looking for.
                      </DialogDescription>
                    </DialogHeader>
                    
                    {sellerProfile.custom_order_description && (
                      <div className="bg-teal-50 border border-teal-100 rounded-sm p-3 text-sm text-teal-700">
                        <p className="font-medium mb-1">About {listing.seller_name}'s custom work:</p>
                        <p className="text-teal-600">{sellerProfile.custom_order_description}</p>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="custom-description">What would you like?</Label>
                        <Textarea
                          id="custom-description"
                          value={customOrderData.description}
                          onChange={(e) => setCustomOrderData({ ...customOrderData, description: e.target.value })}
                          placeholder="Describe what you're looking for... size, style, materials, colors, etc."
                          rows={4}
                          className="mt-1"
                          data-testid="custom-order-description-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="custom-budget">Budget (optional)</Label>
                        <Input
                          id="custom-budget"
                          type="number"
                          value={customOrderData.budget}
                          onChange={(e) => setCustomOrderData({ ...customOrderData, budget: e.target.value })}
                          placeholder="$"
                          className="mt-1"
                          data-testid="custom-order-budget-input"
                        />
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button
                        onClick={handleCustomOrderRequest}
                        disabled={sendingCustomOrder}
                        className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                        data-testid="send-custom-order-btn"
                      >
                        {sendingCustomOrder ? (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          'Send Request'
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {/* Seller Actions - Mark as Sold, Edit, Delete */}
              {user?.id === listing.seller_id && (
                <div className="space-y-2 pt-4 border-t border-stone-200">
                  <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-2">Seller Actions</p>
                  
                  {listing.status !== 'sold' ? (
                    <Button
                      onClick={handleMarkSold}
                      disabled={markingSold}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {markingSold ? (
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Mark as Sold
                    </Button>
                  ) : (
                    <Button
                      onClick={handleMarkUnsold}
                      disabled={markingSold}
                      variant="outline"
                      className="w-full border-green-600 text-green-600 hover:bg-green-50"
                    >
                      {markingSold ? (
                        <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full mr-2" />
                      ) : (
                        <Tag className="h-4 w-4 mr-2" />
                      )}
                      Mark as Available
                    </Button>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/edit-listing/${id}`)}
                      variant="outline"
                      className="flex-1"
                    >
                      Edit Listing
                    </Button>
                    <Button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this listing?')) {
                          try {
                            await api().delete(`/listings/${id}`);
                            toast.success('Listing deleted');
                            navigate('/dashboard');
                          } catch (err) {
                            toast.error('Failed to delete listing');
                          }
                        }
                      }}
                      variant="outline"
                      className="flex-1 text-teal-600 border-teal-200 hover:bg-teal-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Seller Info */}
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-teal-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">{listing.seller_name}</p>
                  <SellerRatingSummary sellerId={listing.seller_id} />
                </div>
                {sellerProfile?.accepts_custom_orders && (
                  <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
                    Takes Custom Orders
                  </span>
                )}
              </div>
            </div>

            {/* Report Listing - For logged-in users who are not the seller */}
            {user && user.id !== listing.seller_id && (
              <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogTrigger asChild>
                  <button
                    className="text-xs text-stone-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                    data-testid="report-listing-btn"
                  >
                    <Flag className="h-3 w-3" />
                    Report this listing
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Report Listing
                    </DialogTitle>
                    <DialogDescription>
                      If you believe this listing may violate our policies or Australian laws, please let us know. The seller will receive a warning to review their listing.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="report-reason">Reason for report *</Label>
                      <Select value={reportReason} onValueChange={setReportReason}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a reason..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="safety_standards">May not meet safety standards</SelectItem>
                          <SelectItem value="intellectual_property">Possible trademark/copyright issue</SelectItem>
                          <SelectItem value="prohibited_item">Prohibited item</SelectItem>
                          <SelectItem value="misleading_description">Misleading description</SelectItem>
                          <SelectItem value="food_safety">Food safety concern</SelectItem>
                          <SelectItem value="other">Other compliance concern</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="report-details">Additional details (optional)</Label>
                      <Textarea
                        id="report-details"
                        value={reportDetails}
                        onChange={(e) => setReportDetails(e.target.value)}
                        placeholder="Please provide any additional context..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                      <p className="font-medium mb-1">What happens when you report:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>The seller receives a warning notification</li>
                        <li>They are asked to verify their listing complies with our policies</li>
                        <li>Our team may review the listing</li>
                        <li>Your identity is kept confidential from the seller</li>
                      </ul>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setReportOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleReportListing}
                      disabled={submittingReport || !reportReason}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {submittingReport ? (
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        'Submit Report'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Description */}
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Description
              </h3>
              <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                {listing.description}
              </p>
            </div>

            {/* Details */}
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Details
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-stone-500">Category</dt>
                  <dd className="text-stone-900 capitalize">{listing.category?.replace('-', ' ')}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Condition</dt>
                  <dd className="text-stone-900 capitalize">{listing.condition?.replace('-', ' ')}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Listed</dt>
                  <dd className="text-stone-900">{formatDate(listing.created_at)}</dd>
                </div>
                {listing.is_restoration && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Type</dt>
                    <dd className="text-teal-600 font-medium">Restoration Project</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Reviews Section */}
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Star className="h-5 w-5 inline mr-2 text-yellow-400" />
                Seller Reviews
              </h3>
              
              {/* Leave a Review - only for buyers who have interacted */}
              {user && user.id !== listing.seller_id && (
                <div className="mb-6">
                  <ReviewForm 
                    sellerId={listing.seller_id} 
                    sellerName={listing.seller_name}
                    listingId={listing.id}
                    onSuccess={() => {
                      // Refresh the page to show new review
                      window.location.reload();
                    }}
                  />
                </div>
              )}
              
              {/* Reviews List */}
              <ReviewsList sellerId={listing.seller_id} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetailPage;
